import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { FadeUp, MaskText } from "@/components/Reveal";
import { PROJECTS } from "@/data/content";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerClose,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, X } from "lucide-react";

export const Projects = () => {
    const [active, setActive] = useState(null);
    const ref = useRef(null);
    const headerRef = useRef(null);

    const { scrollYProgress: sectionScroll } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const sectionProgress = useSpring(sectionScroll, {
        stiffness: 90,
        damping: 26,
        mass: 0.4,
    });
    // Smoothed ambient orb motion
    const orbY1 = useTransform(sectionProgress, [0, 1], [-60, 60]);
    const orbY2 = useTransform(sectionProgress, [0, 1], [40, -80]);
    const orbX = useTransform(sectionProgress, [0, 1], [-30, 30]);

    // Header settles BEFORE reaching top — driven by its own element scroll
    const { scrollYProgress: headerScroll } = useScroll({
        target: headerRef,
        offset: ["start 0.95", "start 0.55"],
    });
    const headerSmooth = useSpring(headerScroll, {
        stiffness: 140,
        damping: 28,
        mass: 0.5,
    });
    const headerY = useTransform(headerSmooth, [0, 1], [40, 0]);
    const headerOpacity = useTransform(headerSmooth, [0, 1], [0, 1]);

    const featured = PROJECTS.filter((p) => p.status === "Released");
    const upcoming = PROJECTS.filter((p) => p.status === "Upcoming");

    return (
        <section
            id="projects"
            ref={ref}
            data-testid="projects-section"
            className="relative py-28 sm:py-36 lg:py-40 bg-[hsl(var(--bg-base))] overflow-hidden"
        >
            {/* Scroll-driven ambient gradient orbs */}
            <motion.div
                aria-hidden
                style={{ y: orbY1, x: orbX }}
                className="absolute top-0 -left-32 w-[720px] h-[720px] pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(18,86%,58%,0.16), transparent 60%)",
                        filter: "blur(48px)",
                    }}
                />
            </motion.div>
            <motion.div
                aria-hidden
                style={{ y: orbY2 }}
                className="absolute top-1/3 -right-32 w-[640px] h-[640px] pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(176,62%,44%,0.14), transparent 60%)",
                        filter: "blur(48px)",
                    }}
                />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    ref={headerRef}
                    style={{ y: headerY, opacity: headerOpacity }}
                    className="flex items-baseline justify-between gap-6 mb-14"
                >
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                        <span className="text-[hsl(var(--accent-gold))]">❖</span> 03 /
                        Projects
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                        Selected Work
                    </div>
                </motion.div>

                <MaskText
                    as="h2"
                    lines={["Stories Built", "For the World."]}
                    className="font-display text-[clamp(2.2rem,7vw,5.5rem)] leading-[0.95] mb-16"
                />

                {/* Featured */}
                <div className="grid grid-cols-12 gap-8 lg:gap-10">
                    {featured.map((p, idx) => (
                        <FadeUp
                            key={p.slug}
                            delay={idx * 0.08}
                            className="col-span-12 lg:col-span-6"
                        >
                            <ProjectCard project={p} onOpen={() => setActive(p)} />
                        </FadeUp>
                    ))}
                </div>

                {/* Upcoming */}
                <div className="mt-24">
                    <FadeUp>
                        <div className="flex items-baseline justify-between gap-6 mb-10">
                            <h3 className="font-display text-2xl uppercase tracking-[0.16em]">
                                Coming Soon
                            </h3>
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                Currently in Development
                            </span>
                        </div>
                    </FadeUp>
                    <div className="grid grid-cols-12 gap-6">
                        {upcoming.map((p, idx) => (
                            <FadeUp
                                key={p.slug}
                                delay={idx * 0.08}
                                className="col-span-12 sm:col-span-6"
                            >
                                <ProjectCard project={p} onOpen={() => setActive(p)} compact />
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </div>

            {/* Drawer */}
            <Drawer open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DrawerContent
                    data-testid="project-detail-drawer"
                    className="bg-[hsl(var(--bg-elevated))] border-t border-white/10 text-white"
                >
                    {active && (
                        <div className="max-w-5xl mx-auto w-full px-5 sm:px-8 pb-14">
                            <div className="flex items-start justify-between gap-6 pt-4">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
                                        {active.status} · {active.year}
                                    </div>
                                    <DrawerHeader className="p-0 mt-2">
                                        <DrawerTitle
                                            className="font-display text-4xl sm:text-6xl tracking-tight leading-none"
                                            data-testid="drawer-title"
                                        >
                                            {active.title}
                                        </DrawerTitle>
                                        <DrawerDescription className="font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))] mt-3">
                                            {active.role}
                                            {active.platform ? ` · ${active.platform}` : ""}
                                        </DrawerDescription>
                                    </DrawerHeader>
                                </div>
                                <DrawerClose asChild>
                                    <button
                                        data-testid="drawer-close-button"
                                        aria-label="Close project"
                                        className="p-2 rounded-full border border-white/15 hover:border-white/40 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </DrawerClose>
                            </div>

                            <div className="grid grid-cols-12 gap-8 mt-10">
                                <div className="col-span-12 md:col-span-7">
                                    {active.image ? (
                                        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black">
                                            <img
                                                src={active.image}
                                                alt={active.title}
                                                className="w-full max-h-[60vh] object-contain"
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className="relative overflow-hidden rounded-xl border border-white/10 bg-black flex items-center justify-center"
                                            style={{ aspectRatio: "1 / 1" }}
                                        >
                                            <div className="text-center">
                                                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))] mb-4">
                                                    ❖ Coming Soon
                                                </div>
                                                <h3 className="font-display text-5xl sm:text-7xl tracking-tight">
                                                    {active.title}
                                                </h3>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="col-span-12 md:col-span-5">
                                    <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                                        {active.description}
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {active.tags.map((t) => (
                                            <Badge
                                                key={t}
                                                variant="outline"
                                                className="border-white/15 text-white font-mono tracking-[0.18em] uppercase text-[10px]"
                                            >
                                                {t}
                                            </Badge>
                                        ))}
                                    </div>
                                    {active.imdb && (
                                        <a
                                            href={active.imdb}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`drawer-imdb-link-${active.slug}`}
                                            className="mt-7 inline-flex items-center gap-3 px-5 py-3 rounded-sm bg-[hsl(var(--accent-gold))] text-black font-mono text-[10px] uppercase tracking-[0.28em] hover:bg-white transition-colors"
                                        >
                                            View on IMDb
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </DrawerContent>
            </Drawer>
        </section>
    );
};

const ProjectCard = ({ project, onOpen, compact = false }) => {
    const isUpcoming = project.status === "Upcoming";
    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
        }
    };
    return (
        <motion.div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={onKey}
            data-testid={`project-card-${project.slug}`}
            className="cine-card text-left w-full rounded-2xl overflow-hidden group cursor-pointer"
            whileTap={{ scale: 0.995 }}
            data-cursor="hover"
        >
            <div
                className="relative overflow-hidden bg-black"
                style={{ aspectRatio: "1 / 1" }}
            >
                {isUpcoming ? (
                    <ComingSoonArtwork title={project.title} />
                ) : (
                    <>
                        <img
                            src={project.image}
                            alt={project.title}
                            loading="lazy"
                            className="w-full h-full object-cover brightness-[0.92] group-hover:brightness-100 transition-all duration-700 scale-[1.03] group-hover:scale-100"
                        />
                        {/* subtle bottom-fade for readability of metadata strip */}
                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 to-transparent pointer-events-none" />
                    </>
                )}
                <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/20 bg-black/50 group-hover:border-white/60 group-hover:bg-[hsl(var(--accent-gold))] group-hover:text-black transition-colors">
                        <ArrowUpRight className="h-4 w-4" />
                    </span>
                </div>
            </div>
            <div className="p-5 sm:p-7 flex items-start justify-between gap-6">
                <div className="min-w-0">
                    <h3 className="font-display text-2xl sm:text-3xl tracking-tight leading-[1] mb-2">
                        {project.title}
                    </h3>
                    <p className="text-[hsl(var(--text-secondary))] text-sm leading-relaxed max-w-md">
                        {project.tagline}
                    </p>
                    {project.imdb && (
                        <a
                            href={project.imdb}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`project-imdb-link-${project.slug}`}
                            className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] hover:text-white transition-colors"
                        >
                            <span className="inline-flex items-center justify-center px-1.5 py-[2px] text-[9px] font-bold tracking-[0.12em] text-black bg-[hsl(var(--accent-gold))] rounded-[2px]">
                                IMDb
                            </span>
                            View on IMDb
                            <ArrowUpRight className="h-3 w-3" />
                        </a>
                    )}
                </div>
                <div className="shrink-0 text-right">
                    {project.platform && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                            {project.platform}
                        </div>
                    )}
                    <div className={`${project.platform ? "mt-1 " : ""}font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]/70`}>
                        {project.year}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Pure text artwork for upcoming projects — no images.
const ComingSoonArtwork = ({ title }) => (
    <div className="absolute inset-0 flex items-center justify-center bg-black">
        {/* Very faint rotating atom motif for brand continuity */}
        <motion.div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            animate={{ rotate: 360 }}
            transition={{ duration: 160, ease: "linear", repeat: Infinity }}
        >
            <svg
                viewBox="0 0 200 200"
                className="w-[78%] h-[78%] opacity-[0.07]"
                fill="none"
            >
                <ellipse cx="100" cy="100" rx="80" ry="32" stroke="hsl(0 0% 96%)" strokeWidth="1" />
                <ellipse
                    cx="100"
                    cy="100"
                    rx="80"
                    ry="32"
                    stroke="hsl(0 0% 96%)"
                    strokeWidth="1"
                    transform="rotate(60 100 100)"
                />
                <ellipse
                    cx="100"
                    cy="100"
                    rx="80"
                    ry="32"
                    stroke="hsl(0 0% 96%)"
                    strokeWidth="1"
                    transform="rotate(120 100 100)"
                />
                <circle cx="100" cy="100" r="6" fill="hsl(38 92% 62%)" />
            </svg>
        </motion.div>
        {/* Subtle vignette */}
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
            }}
        />
        <div className="relative z-10 text-center px-6">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 border border-white/15 rounded-sm bg-black/40">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent-gold))] animate-pulse" />
                <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                    Coming Soon · TBA
                </span>
            </div>
            <h3 className="font-display text-5xl sm:text-7xl tracking-tight leading-none text-white">
                {title}
            </h3>
        </div>
    </div>
);
